import { Node } from 'reactflow';
import { subLayersNodeIdsByMode } from '@constants/canvas/layerNodes';
import { Modes } from '@constants/canvas/layers';
import {
  matchesModelFilters,
  ModelFilters,
} from '@context/ProjectFilters/matchesModelFilters';
import { CiaFilters } from '@context/ProjectFilters/types';
import type { ModelFilterCategory } from '@store/zustand/model-filters/types';

export type CiaFilterCategory = keyof CiaFilters;

export type FlatModelFilterCategory = Exclude<
  ModelFilterCategory,
  CiaFilterCategory
>;

/**
 * The values the popover offers, per category. A flat category lists its
 * values; a CIA category lists the answers per criteria id, because the unit
 * the popover selects by for Assurance, Coverage and Applicability is one
 * criteria's answer, never the answer string on its own.
 */
export type ModelFilterVocabulary = Partial<
  Record<FlatModelFilterCategory, string[]> &
    Record<CiaFilterCategory, Record<string, string[]>>
>;

/**
 * Counts shaped like the vocabulary they were taken from: a flat category
 * maps value to count, a CIA category maps criteria id to answer to count.
 */
export type ModelFilterCounts = Partial<
  Record<FlatModelFilterCategory, Record<string, number>> &
    Record<CiaFilterCategory, Record<string, Record<string, number>>>
>;

const CIA_CATEGORIES: ReadonlyArray<CiaFilterCategory> = [
  'assurance_level',
  'coverage',
  'applicability',
];

const isCiaCategory = (
  category: ModelFilterCategory,
): category is CiaFilterCategory =>
  CIA_CATEGORIES.includes(category as CiaFilterCategory);

const emptyFilters = (): ModelFilters => ({
  domains: [],
  validationStatus: [],
  reviewStatus: [],
  operationalStatuses: [],
  operatingModelStatuses: [],
  partners: [],
  ciaFilters: { assurance_level: {}, coverage: {}, applicability: {} },
});

/**
 * The filter state in which one flat value, and nothing else, is ticked.
 */
const soleFlatSelection = (
  category: FlatModelFilterCategory,
  value: string,
): ModelFilters => ({ ...emptyFilters(), [category]: [value] });

/**
 * The filter state in which one criteria's answer, and nothing else, is
 * ticked.
 */
const soleCiaSelection = (
  category: CiaFilterCategory,
  criteriaId: string,
  answer: string,
): ModelFilters => {
  const empty = emptyFilters();

  return {
    ...empty,
    ciaFilters: {
      ...empty.ciaFilters,
      [category]: { [criteriaId]: [answer] },
    },
  };
};

/**
 * Per-value entity counts for the filter popover, scoped to one mode.
 *
 * A count is the number of in-mode nodes `matchesModelFiltersDto` keeps when
 * that value alone is selected. It is taken by running the predicate rather
 * than by reading DTO fields, so every rule the predicate applies — Scope
 * matching `direct` rows only, the sub-layers that carry no validation
 * status, the scope-only sub-layers hidden by any other category, CIA
 * skipping unreported nodes — holds for the counts by construction. The one
 * thing a count cannot see is the graph pass the canvas adds for the
 * No-operationalised Scope row, which keeps the ancestors of a matching node
 * visible without matching them.
 *
 * Counts the unfiltered set: AC5.2 hides counts the moment any value is
 * ticked and never recalculates, so there is no filtered variant to compute.
 * Every value in the supplied vocabulary gets an entry — a value carried by
 * no entity reads 0 and stays selectable (AC5.1.1), which only works because
 * the caller owns the vocabulary rather than the data. A CIA count is keyed
 * by criteria id first, so a node answering "High" on two criteria counts
 * once under each rather than twice under "High".
 */
export const countNodesByValue = (
  nodes: Node[],
  mode: Modes,
  vocabulary: ModelFilterVocabulary,
): ModelFilterCounts => {
  const subLayers = subLayersNodeIdsByMode[mode] ?? [];
  const inMode = nodes.filter((node) =>
    subLayers.includes(node.parentNode ?? ''),
  );

  const kept = (filters: ModelFilters) =>
    inMode.filter((node) => matchesModelFilters(node, filters)).length;

  return (
    Object.keys(vocabulary) as ModelFilterCategory[]
  ).reduce<ModelFilterCounts>((acc, category) => {
    if (isCiaCategory(category)) {
      const answersByCriteria = vocabulary[category] ?? {};

      const counts = Object.fromEntries(
        Object.entries(answersByCriteria).map(([criteriaId, answers]) => [
          criteriaId,
          Object.fromEntries(
            answers.map((answer) => [
              answer,
              kept(soleCiaSelection(category, criteriaId, answer)),
            ]),
          ),
        ]),
      );

      return { ...acc, [category]: counts };
    }

    const values = vocabulary[category] ?? [];

    const counts = Object.fromEntries(
      values.map((value) => [value, kept(soleFlatSelection(category, value))]),
    );

    return { ...acc, [category]: counts };
  }, {});
};
